import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createFlightOfferSchema, type CreateFlightOfferInput } from '@wingseat/shared';
import { useAircraft, useAircraftTypes } from '../hooks/use-aircraft';
import { useEvents } from '../hooks/use-events';
import { useCreateFlight } from '../hooks/use-flights';
import { ApiError } from '../lib/api-client';
import { formatDate, fromDateTimeLocal, toDateTimeLocal } from '../lib/format';

type AircraftMode = 'list' | 'freetext';

function defaultStart(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toDateTimeLocal(d);
}
function defaultEnd(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(13, 0, 0, 0);
  return toDateTimeLocal(d);
}

export function NewFlightPage() {
  const navigate = useNavigate();
  const aircraft = useAircraft();
  const types = useAircraftTypes();
  const events = useEvents(true); // nur zukuenftige
  const createFlight = useCreateFlight();

  const [mode, setMode] = useState<AircraftMode>('list');
  const [aircraftId, setAircraftId] = useState('');
  const [registrationText, setRegistrationText] = useState('');
  const [aircraftTypeId, setAircraftTypeId] = useState('');
  const [startAt, setStartAt] = useState(defaultStart());
  const [endAt, setEndAt] = useState(defaultEnd());
  const [fromLocation, setFromLocation] = useState('EDAZ');
  const [toLocation, setToLocation] = useState('EDAZ');
  const [freeSeats, setFreeSeats] = useState(1);
  const [sharedPic, setSharedPic] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [roundTrip, setRoundTrip] = useState(false);
  const [costShareText, setCostShareText] = useState('');
  const [comment, setComment] = useState('');
  const [eventId, setEventId] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const input: CreateFlightOfferInput = {
      aircraftId: mode === 'list' ? aircraftId || null : null,
      aircraftRegistrationText:
        mode === 'freetext' ? registrationText.trim() || null : null,
      aircraftTypeId:
        mode === 'freetext' && aircraftTypeId ? aircraftTypeId : null,
      startAt: fromDateTimeLocal(startAt),
      endAt: fromDateTimeLocal(endAt),
      fromLocation: fromLocation.trim(),
      toLocation: toLocation.trim(),
      freeSeats,
      sharedPic,
      favoritesOnly,
      roundTrip,
      costShareText: costShareText.trim() || null,
      comment: comment.trim() || null,
      eventId: eventId || null,
    };
    const parsed = createFlightOfferSchema.safeParse(input);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const iss of parsed.error.issues) {
        const k = iss.path[0]?.toString();
        if (k && !errs[k]) errs[k] = iss.message;
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    createFlight.mutate(parsed.data, {
      onSuccess: (data) => {
        navigate(`/app/flights/${data.id}`, { replace: true });
      },
    });
  };

  const apiErr =
    createFlight.error instanceof ApiError
      ? createFlight.error.message
      : createFlight.error
        ? 'Anlegen fehlgeschlagen'
        : null;

  return (
    <form onSubmit={onSubmit} className="px-4 py-4 space-y-4">
      <h1 className="text-xl font-semibold">Neuer Flug</h1>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-3">
        <div className="flex gap-2">
          <ModeButton active={mode === 'list'} onClick={() => setMode('list')}>
            Flugzeug aus Liste
          </ModeButton>
          <ModeButton
            active={mode === 'freetext'}
            onClick={() => setMode('freetext')}
          >
            Freitext-Kennung
          </ModeButton>
        </div>

        {mode === 'list' ? (
          <Field label="Flugzeug" error={fieldErrors['aircraftId']}>
            <select
              value={aircraftId}
              onChange={(e) => setAircraftId(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            >
              <option value="">— wählen —</option>
              {aircraft.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.registration} ({a.type?.name ?? '—'})
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <>
            <Field
              label="Kennung"
              error={fieldErrors['aircraftRegistrationText']}
            >
              <input
                value={registrationText}
                onChange={(e) => setRegistrationText(e.target.value)}
                placeholder="z. B. D-KGAS"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
              />
            </Field>
            <Field label="Flugzeugtyp (optional)">
              <select
                value={aircraftTypeId}
                onChange={(e) => setAircraftTypeId(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
              >
                <option value="">— kein Typ —</option>
                {types.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start" error={fieldErrors['startAt']}>
            <input
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              className="w-full min-w-0 appearance-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </Field>
          <Field label="Ende" error={fieldErrors['endAt']}>
            <input
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className="w-full min-w-0 appearance-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Von" error={fieldErrors['fromLocation']}>
            <input
              value={fromLocation}
              onChange={(e) => setFromLocation(e.target.value)}
              className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base uppercase"
            />
          </Field>
          <Field label="Nach" error={fieldErrors['toLocation']}>
            <input
              value={toLocation}
              onChange={(e) => setToLocation(e.target.value)}
              className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base uppercase"
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Freie Plätze" error={fieldErrors['freeSeats']}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFreeSeats((n) => Math.max(1, n - 1))}
                className="h-8 w-8 rounded-md border border-slate-700 bg-slate-800 text-base"
              >
                −
              </button>
              <span className="text-base font-semibold w-6 text-center">
                {freeSeats}
              </span>
              <button
                type="button"
                onClick={() => setFreeSeats((n) => Math.min(20, n + 1))}
                className="h-8 w-8 rounded-md border border-slate-700 bg-slate-800 text-base"
              >
                +
              </button>
            </div>
          </Field>
          <label
            className="flex items-center gap-2 pb-2 text-sm select-none cursor-pointer"
            title="Wenn aktiv: Flug nur sichtbar fuer User, die du als Favorit markiert hast"
          >
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={(e) => setFavoritesOnly(e.target.checked)}
              className="h-5 w-5 rounded border-slate-600 bg-slate-950 accent-brand-600"
            />
            <span className="text-slate-300">
              Nur <span className="text-amber-300">★</span>
            </span>
          </label>
          <label
            className="flex items-center gap-2 pb-2 text-sm select-none cursor-pointer"
            title="Shared PiC: beide Piloten teilen sich die Verantwortung als Pilot in Command"
          >
            <input
              type="checkbox"
              checked={sharedPic}
              onChange={(e) => setSharedPic(e.target.checked)}
              className="h-5 w-5 rounded border-slate-600 bg-slate-950 accent-brand-600"
            />
            <span className="text-slate-300">Shared PiC</span>
          </label>
          <label
            className="flex items-center gap-2 pb-2 text-sm select-none cursor-pointer"
            title="Hin- und Rueckflug — Anzeige als Start → Ziel → Start"
          >
            <input
              type="checkbox"
              checked={roundTrip}
              onChange={(e) => setRoundTrip(e.target.checked)}
              className="h-5 w-5 rounded border-slate-600 bg-slate-950 accent-brand-600"
            />
            <span className="text-lg leading-none text-slate-300" aria-label="Hin- und Rueckflug">
              ⇄
            </span>
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-3">
        <Field label="Kostenanteil (optional)">
          <input
            value={costShareText}
            onChange={(e) => setCostShareText(e.target.value)}
            placeholder="z. B. 40 €/h"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </Field>
        <Field label="Kommentar (optional)">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </Field>
        <Field label="Event (optional)">
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          >
            <option value="">— keinem Event zugeordnet —</option>
            {events.data?.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name} ({formatDate(ev.startAt)})
              </option>
            ))}
          </select>
        </Field>
      </section>

      {apiErr && (
        <div className="rounded-lg border border-rose-900 bg-rose-950/60 px-3 py-2 text-sm text-rose-200">
          {apiErr}
        </div>
      )}

      <button
        type="submit"
        disabled={createFlight.isPending}
        className="w-full rounded-lg bg-brand-600 px-4 py-3 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
      >
        {createFlight.isPending ? 'Wird gespeichert …' : 'Flug einstellen'}
      </button>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    // min-w-0: Grid-Items default min-width:auto wuerde sonst iOS-Safari
    // den datetime-local-Input ueber die Spaltenbreite ausdehnen lassen.
    <div className="space-y-1 min-w-0">
      <label className="block text-xs font-medium text-slate-400">{label}</label>
      {children}
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? 'border-brand-500 bg-brand-600/20 text-brand-200'
          : 'border-slate-700 bg-slate-800 text-slate-400'
      }`}
    >
      {children}
    </button>
  );
}
